import React from 'react';

import { localeDetails } from '~/components/CmsMain/localeGlobals';
import COUNTRY_TO_STATE_MAP from '~/server_shared/generated-types/COUNTRY_TO_STATE_MAP';

import { getDateFromServerDateTime } from '../../../../DateTimeUtils';
import { MainCard, PropertyDetails } from '../../../core';
import { PropertyDetailsRow } from '../../../core/NewDesignSystem/PropertyDetails/PropertyDetailsRow';
import { LegalActionEditButton } from '../../components/LegalActionEditButton';
import { EditJurisdictionInformationDialog } from '../../Dialogs/EditJurisdictionInformation/EditJurisdictionInformationDialog';
import { JURISDICTION_INFORMATION_FIELDS } from '../../FormFragments/FormFieldsDefinitions';
import { useLegalAction } from '../../hooks/useLegalAction';

import { DisputeResolutionProfessionalsCard } from './DisputeResolutionProfessionals/DisputeResolutionProfessionalsCard';

export const JurisdictionCard: React.FC = () => {
  const { legalAction, reloadLegalAction } = useLegalAction();
  const [isEditJurisdictionDialogOpen, setIsEditJurisdictionDialogOpen] = React.useState(false);

  // Prefer the org's region first so codes shared across countries (e.g. WA =
  // Washington in US, Western Australia in AU) resolve to the right territory.
  // Fall back to a cross-country search for resilience (e.g. a US legal action
  // viewed by a non-US-region org).
  const stateCode = legalAction?.jurisdiction_state ?? '';
  // localeDetails is exported from a .js module typed as `null` until populated
  // at boot; cast through to read the region.
  const region = (localeDetails as { locale?: { region?: string } } | null)?.locale?.region;
  const countryToStateMap = COUNTRY_TO_STATE_MAP as Record<string, Record<string, string>>;
  const stateName =
    (region && countryToStateMap[region]?.[stateCode]) ||
    Object.values(countryToStateMap)
      .map((states) => states[stateCode])
      .find(Boolean) ||
    stateCode;

  return legalAction ? (
    <>
      <MainCard
        type="elevated"
        title="Jurisdiction Information"
        action={<LegalActionEditButton onClick={() => setIsEditJurisdictionDialogOpen(true)} />}
        collapsible
        openByDefault
      >
        <div className="flex w-1/2">
          <PropertyDetailsRow>
            <PropertyDetails title={JURISDICTION_INFORMATION_FIELDS.jurisdiction.label} text={stateName} />
            <PropertyDetails title={JURISDICTION_INFORMATION_FIELDS.venue.label} text={legalAction.venue} />
          </PropertyDetailsRow>
        </div>
        <div className="flex w-1/2">
          <PropertyDetailsRow>
            <PropertyDetails title={JURISDICTION_INFORMATION_FIELDS.court.label} text={legalAction.court} />
            <PropertyDetails
              title={JURISDICTION_INFORMATION_FIELDS.dateFilledInCourt.label}
              text={legalAction.date_filled_in_court ? getDateFromServerDateTime(legalAction.date_filled_in_court) : ''}
            />
          </PropertyDetailsRow>
        </div>
        <DisputeResolutionProfessionalsCard />
      </MainCard>
      {isEditJurisdictionDialogOpen ? (
        <EditJurisdictionInformationDialog
          legalAction={legalAction}
          onClose={() => setIsEditJurisdictionDialogOpen(false)}
          onSubmitSuccess={reloadLegalAction}
        />
      ) : null}
    </>
  ) : null;
};
